import '../i18n/config';
import { ErrorBoundary } from "react-error-boundary";
import {
  browserInfo,
  mapToArr,
  mediateMap,
  readFileFromIndexedDB,
  setLocalStorage,
} from "../Common/util";
import {
  CardShowing,
  ChangeSteps,
  InsertInfo,
  LineChanges,
  LineProps,
  RecordType,
  StationProps,
  UserDataType,
  initData,
  setDataFromJson,
} from "../Data/UserData";
import { FunctionMode, Mode } from "../DataStructure/Mode";
import { Cards } from "../Render/Card/Cards";
import {
  DeleteConfirmation,
  showConfirmationInterface,
} from "../Render/Delete/DeleteConfirmation";
import { Menu } from "../Render/Header/Menu";
import ScaleLayer from "../Render/Layer/ScaleLayer";
import { WelcomeTour } from "../WelcomeTour/WelcomeTour";
import "./App.scss";
import "driver.js/dist/driver.css";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { ErrorFallback } from "../Render/ErrorFallback/ErrorFallback";
import { Recovery } from "../Render/Recovery/Recovery";
import { getExistMap } from "../Common/api";
import { hashTopology } from "../simulation";
import { LiveTrainsDrawer } from "../Render/LiveTrains/LiveTrainsDrawer";
import { MobileTrainTooltip } from "../Render/LiveTrains/MobileTrainTooltip";
import { crawlMerseyrailLiveTrains } from "../liveTrains/realtimeTrains";
import { LiveTrainPin, LiveTrainStatus } from "../liveTrains/types";

function App() {
  const [editingMode, setEditingMode] = useState(Mode.normal);
  const [functionMode, setFunctionMode] = useState(FunctionMode.normal);
  const [record, setRecord] = useState<RecordType>([]);
  const [currentRecordIndex, setCurrentRecordIndex] = useState(-1);
  const [insertInfo, setInsertInfo] = useState<InsertInfo>();
  const [data, setDataOriginal] = useState(initData);
  const [showName, setShowName] = useState(true);
  const [autoHiddenName, setAutoHiddenName] = useState(true);
  const [drawing, setDrawing] = useState(false);
  const [translateX, setTranslateX] = useState(0);
  const [translateY, setTranslateY] = useState(0);
  const [page, setPage] = useState("title");
  const [scale, setScale] = useState(1);
  const ref = useRef<any>();
  const menuRef = useRef();
  const [saved, setSaved] = useState(true);
  const [defaultShape, setDefaultShape] = useState('cicle');
  const [showTour, setShowTour] = useState(false);
  const [recoveredFromError, setRecoveredFromError] = useState(false);
  const [showConfirmation, setShowConfirmation] =
    useState<showConfirmationInterface>();
  const [liveTrains, setLiveTrains] = useState<LiveTrainStatus[]>([]);
  const [liveTrainPins, setLiveTrainPins] = useState<LiveTrainPin[]>([]);
  const [liveServiceCount, setLiveServiceCount] = useState(0);
  const [liveUpdatedAtMs, setLiveUpdatedAtMs] = useState<number>();
  const [liveLoading, setLiveLoading] = useState(false);
  const [liveError, setLiveError] = useState<string>();
  const [selectedLiveTrainId, setSelectedLiveTrainId] = useState<string>();
  const [mobileTrainTooltipOpen, setMobileTrainTooltipOpen] = useState(false);
  const [isMobileViewport, setIsMobileViewport] = useState(
    () => window.innerWidth <= 900
  );
  const mobileAutoZoomAppliedRef = useRef(false);
  const dataRef = useRef(data);
  const livePinMapRef = useRef<Map<string, LiveTrainPin>>(new Map());
  // keep latest data if crash happend
  const setData = (data: React.SetStateAction<UserDataType>) => {
    if (typeof data === "function") {
      setDataOriginal((state) => {
        const newState = data(state);
        setLocalStorage(newState, () => setSaved(false));
        return newState;
      });
    } else {
      setLocalStorage(data, () => setSaved(false));
      setDataOriginal(data);
    }
  };
  useEffect(() => {
    setShowConfirmation(() => ref.current?.showConfirmation);
  }, [ref.current?.showConfirmation]);
  const [cardShowing, setCardShowing] = useState(new CardShowing());
  const transfromTools = {
    scale,
    setScale,
    translateX,
    translateY,
    setTranslateX,
    setTranslateY,
  };
  const {backgroundColor} = data;
  const topologyHash = useMemo(() => hashTopology(data), [data]);
  useEffect(() => {
    dataRef.current = data;
  }, [data]);

  const isLiverpoolMap = useMemo(() => {
    const names = Array.from(data.stations.values()).map((station) =>
      station.stationName.toLowerCase()
    );
    return (
      data.stations.size > 30 &&
      names.includes("liverpool central") &&
      names.includes("southport")
    );
  }, [data]);

  const selectedLiveTrain = useMemo(
    () => liveTrains.find((train) => train.id === selectedLiveTrainId),
    [liveTrains, selectedLiveTrainId]
  );

  const handleLiveTrainPinSelect = (trainId: string) => {
    setSelectedLiveTrainId(trainId);
    if (isMobileViewport) {
      setMobileTrainTooltipOpen(true);
    }
  };

  useEffect(() => {
    const onResize = () => setIsMobileViewport(window.innerWidth <= 900);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    localStorage.setItem("skip-tour-viewed", "Y");
  }, []);

  useEffect(() => {
    document
      .getElementById("theme-color")
      ?.setAttribute("content", backgroundColor ? backgroundColor : "#ffffff");
  }, [backgroundColor])
  useEffect(() => {
    let active = true;
    getExistMap("liverpool-merseyrail")
      .then((json) => {
        if (!active) return;
        const latest = setDataFromJson(setData, json);
        mediateMap(latest, transfromTools);
        setSaved(true);
      })
      .catch((e) => {
        console.error(e);
      });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (!isLiverpoolMap) {
      setLiveTrains([]);
      setLiveTrainPins([]);
      setLiveServiceCount(0);
      setLiveUpdatedAtMs(undefined);
      setLiveError(undefined);
      setSelectedLiveTrainId(undefined);
      setMobileTrainTooltipOpen(false);
      return;
    }

    let active = true;
    let timerId = 0;

    const refresh = async () => {
      setLiveLoading(true);
      try {
        const snapshot = await crawlMerseyrailLiveTrains(dataRef.current);
        if (!active) {
          return;
        }
        setLiveTrains(snapshot.trains);
        setLiveServiceCount(snapshot.serviceCount);
        setLiveUpdatedAtMs(snapshot.updatedAtMs);
        setLiveError(undefined);
        setSelectedLiveTrainId((previous) => {
          if (snapshot.trains.length < 1) {
            return undefined;
          }
          if (previous && snapshot.trains.some((train) => train.id === previous)) {
            return previous;
          }
          return snapshot.trains[0].id;
        });
        if (snapshot.trains.length < 1) {
          setMobileTrainTooltipOpen(false);
        }
      } catch (error) {
        console.error(error);
        if (!active) {
          return;
        }
        setLiveError("Live data fetch failed. Retrying automatically.");
      } finally {
        if (active) {
          setLiveLoading(false);
        }
      }
    };

    refresh();
    timerId = window.setInterval(refresh, 60 * 1000);
    return () => {
      active = false;
      window.clearInterval(timerId);
    };
  }, [isLiverpoolMap, topologyHash]);

  useEffect(() => {
    if (!isMobileViewport) {
      setMobileTrainTooltipOpen(false);
    }
  }, [isMobileViewport]);

  useEffect(() => {
    if (!isLiverpoolMap || !isMobileViewport) {
      mobileAutoZoomAppliedRef.current = false;
      return;
    }
    if (mobileAutoZoomAppliedRef.current) {
      return;
    }

    const targetScale = 0.82;
    if (scale < targetScale) {
      const centerX = window.innerWidth / 2;
      const centerY = window.innerHeight / 2;
      const ratio = targetScale / Math.max(0.01, scale);
      setTranslateX(centerX - ratio * (centerX - translateX));
      setTranslateY(centerY - ratio * (centerY - translateY));
      setScale(targetScale);
    }

    setShowName(true);
    setAutoHiddenName(true);
    mobileAutoZoomAppliedRef.current = true;
  }, [isLiverpoolMap, isMobileViewport, scale, translateX, translateY]);

  useEffect(() => {
    if (!isLiverpoolMap || liveTrains.length < 1) {
      livePinMapRef.current = new Map();
      setLiveTrainPins([]);
      return;
    }

    let rafId = 0;
    let active = true;

    const getTargetPosition = (train: LiveTrainStatus, nowMs: number) => {
      const {
        phase,
        segmentStartMs,
        segmentEndMs,
        segmentFromX,
        segmentFromY,
        segmentToX,
        segmentToY,
      } = train;
      if (
        phase === "BetweenStations" &&
        segmentStartMs &&
        segmentEndMs &&
        segmentEndMs > segmentStartMs &&
        segmentFromX !== undefined &&
        segmentFromY !== undefined &&
        segmentToX !== undefined &&
        segmentToY !== undefined
      ) {
        const progress = Math.max(
          0,
          Math.min(1, (nowMs - segmentStartMs) / Math.max(1, segmentEndMs - segmentStartMs))
        );
        return {
          x: segmentFromX + (segmentToX - segmentFromX) * progress,
          y: segmentFromY + (segmentToY - segmentFromY) * progress,
        };
      }
      return { x: train.x, y: train.y };
    };

    const normalizeAngle = (angleDeg: number) =>
      ((angleDeg % 360) + 360) % 360;

    const lerpAngle = (fromDeg: number, toDeg: number, alpha: number) => {
      const delta = ((toDeg - fromDeg + 540) % 360) - 180;
      return normalizeAngle(fromDeg + delta * alpha);
    };

    const getHeadingDeg = (train: LiveTrainStatus, fallbackDeg?: number) => {
      const fromX = train.segmentFromX ?? train.x;
      const fromY = train.segmentFromY ?? train.y;
      const toX = train.segmentToX ?? train.x;
      const toY = train.segmentToY ?? train.y;
      const dx = toX - fromX;
      const dy = toY - fromY;
      if (Math.abs(dx) > 0.01 || Math.abs(dy) > 0.01) {
        return normalizeAngle((Math.atan2(dy, dx) * 180) / Math.PI);
      }
      if (fallbackDeg !== undefined) {
        return normalizeAngle(fallbackDeg);
      }
      return train.direction === -1 ? 180 : 0;
    };

    const tick = () => {
      if (!active) {
        return;
      }

      const nowMs = Date.now();
      const previousById = livePinMapRef.current;
      const nextPins: LiveTrainPin[] = liveTrains.map((train) => {
        const target = getTargetPosition(train, nowMs);
        const previous = previousById.get(train.id);
        const smoothing = previous ? 0.08 : 1;
        const headingTarget = getHeadingDeg(train, previous?.headingDeg);
        const headingDeg =
          previous?.headingDeg !== undefined
            ? lerpAngle(previous.headingDeg, headingTarget, 0.12)
            : headingTarget;
        const headingRad = (headingDeg * Math.PI) / 180;
        const headingOffsetX = Math.cos(headingRad) * 11;
        const headingOffsetY = Math.sin(headingRad) * 11;
        const x = previous ? previous.x + (target.x - previous.x) * smoothing : target.x;
        const y = previous ? previous.y + (target.y - previous.y) * smoothing : target.y;
        return {
          id: train.id,
          x,
          y,
          direction: train.direction,
          status: train.status,
          label: train.label,
          headingDeg,
          headingOffsetX,
          headingOffsetY,
        };
      });

      livePinMapRef.current = new Map(nextPins.map((pin) => [pin.id, pin]));
      setLiveTrainPins(nextPins);
      rafId = window.requestAnimationFrame(tick);
    };

    tick();
    return () => {
      active = false;
      window.cancelAnimationFrame(rafId);
    };
  }, [isLiverpoolMap, liveTrains]);


  return (
    <ErrorBoundary
      FallbackComponent={ErrorFallback}
      onReset={() => {
        const last = localStorage.getItem("last");
        if (last) {
          const data = setDataFromJson(setData, last);
          readFileFromIndexedDB("image")
            .then((file) => {
              setData((data) => ({
                ...data,
                // backgroundColor: "image",
                backgroundImage: file as File,
              }));
            })
            .catch((e) => {
              console.error(e);
            });
          mediateMap(data, transfromTools);
        }
        setRecoveredFromError(true);
      }}
    >
      <div className="App">
        <Menu
          setEditingMode={setEditingMode}
          functionMode={functionMode}
          setFunctionMode={setFunctionMode}
          record={record}
          setRecord={setRecord}
          currentRecordIndex={currentRecordIndex}
          setCurrentRecordIndex={setCurrentRecordIndex}
          data={data}
          setData={setData}
          ref={menuRef}
          insertInfo={insertInfo}
          setInsertInfo={setInsertInfo}
          showName={showName}
          setShowName={setShowName}
          autoHiddenName={autoHiddenName}
          setAutoHiddenName={setAutoHiddenName}
          drawing={drawing}
          setDrawing={setDrawing}
          cardShowing={cardShowing}
          setCardShowing={setCardShowing}
          translateX={translateX}
          translateY={translateY}
          scale={scale}
          setTranslateX={setTranslateX}
          setTranslateY={setTranslateY}
          setScale={setScale}
          saved={saved}
          setSaved={setSaved}
          showTour={showTour}
          setShowTour={setShowTour}
          page={page}
          setPage={setPage}
          defaultShape={defaultShape}
          setDefaultShape={setDefaultShape}
        />
        <DeleteConfirmation ref={ref} />
        <ScaleLayer
          editingMode={editingMode}
          setEditingMode={setEditingMode}
          data={data}
          setData={setData}
          functionMode={functionMode}
          setFunctionMode={setFunctionMode}
          record={record}
          setRecord={setRecord}
          currentRecordIndex={currentRecordIndex}
          setCurrentRecordIndex={setCurrentRecordIndex}
          insertInfo={insertInfo}
          setInsertInfo={setInsertInfo}
          cardShowing={cardShowing}
          setCardShowing={setCardShowing}
          showName={showName}
          setShowName={setShowName}
          autoHiddenName={autoHiddenName}
          setAutoHiddenName={setAutoHiddenName}
          drawing={drawing}
          setDrawing={setDrawing}
          translateX={translateX}
          translateY={translateY}
          scale={scale}
          setTranslateX={setTranslateX}
          setTranslateY={setTranslateY}
          setScale={setScale}
          page={page}
          setPage={setPage}
          defaultShape={defaultShape}
          liveTrainPins={liveTrainPins}
          selectedLiveTrainId={selectedLiveTrainId}
          onLiveTrainPinSelect={handleLiveTrainPinSelect}
          isMobileViewport={isMobileViewport}
        />
        <Cards
          functionMode={functionMode}
          setFunctionMode={setFunctionMode}
          data={data}
          setData={setData}
          showConfirmation={showConfirmation}
          menuRef={menuRef}
          insertInfo={insertInfo}
          setInsertInfo={setInsertInfo}
          cardShowing={cardShowing}
          setCardShowing={setCardShowing}
        />
        {isLiverpoolMap ? (
          <LiveTrainsDrawer
            trains={liveTrains}
            loading={liveLoading}
            updatedAtMs={liveUpdatedAtMs}
            serviceCount={liveServiceCount}
            error={liveError}
            selectedTrainId={selectedLiveTrainId}
            onSelectTrain={setSelectedLiveTrainId}
            isMobile={isMobileViewport}
          />
        ) : null}
        {isLiverpoolMap ? (
          <MobileTrainTooltip
            open={isMobileViewport && mobileTrainTooltipOpen}
            train={selectedLiveTrain}
            onClose={() => setMobileTrainTooltipOpen(false)}
          />
        ) : null}
        <WelcomeTour showTour={showTour} setShowTour={setShowTour} />
        <Recovery
          data={data}
          setData={setData}
          recoveredFromError={recoveredFromError}
          setRecoveredFromError={setRecoveredFromError}
          transfromTools={transfromTools}
        />
      </div>
    </ErrorBoundary>
  );
}

export default App;
